"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.zodPortContract = zodPortContract;
exports.isZodPortContract = isZodPortContract;
/**
 * SOLID Review
 * S — Strong. One concern: turn a table of Zod schemas into an `IPortContract`, and derive the
 *     TypeScript interface the port's consumer sees from the same table.
 * O — Strong. A new op is a new row in the table; a new port is a new table. Nothing here changes.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One function, three type helpers, one predicate.
 * D — Good. Depends on `zod` — the one npm dependency in the kernel, isolated on this subpath so
 *     that `@ml3d/kernel` itself stays a leaf. The frontend never imports this file.
 */
const zod_1 = require("zod");
function zodPortContract(config) {
    const local = config.local ?? false;
    const stream = config.stream ?? false;
    const opNames = Object.keys(config.ops).sort();
    // The whole port is rendered as ONE schema so that a registered snapshot is extracted into a
    // single `$defs` entry shared by every op that names it; the ops are then read back out of it.
    const shape = {};
    for (const op of opNames) {
        const schema = config.ops[op];
        // A stream's result is an iterator: nothing to describe, and nothing is parsed.
        shape[op] = stream ? zod_1.z.object({ args: schema.args }) : zod_1.z.object({ args: schema.args, result: schema.result });
    }
    // Every op is an object property of the rendered whole, so the two lookups below always hit.
    const whole = zod_1.z.toJSONSchema(zod_1.z.object(shape), { unrepresentable: 'any' });
    const ops = {};
    for (const op of opNames) {
        const rendered = whole.properties[op].properties;
        ops[op] = { args: rendered['args'], result: stream ? { $comment: 'stream' } : rendered['result'] };
    }
    const defs = whole.$defs ?? {};
    const jsonSchema = { name: config.name, digest: '', local, stream, ops, defs };
    const digest = fnv1a64(JSON.stringify({ name: config.name, local, stream, ops, defs }));
    return {
        name: config.name,
        digest,
        local,
        stream,
        ops: opNames,
        jsonSchema: { ...jsonSchema, digest },
        parseArgs(op, args) {
            const schema = config.ops[op];
            if (schema === undefined)
                throw new Error(`Port '${config.name}' has no op '${op}'.`);
            return schema.args.parse(args);
        },
        parseResult(op, result) {
            const schema = config.ops[op];
            if (schema === undefined)
                throw new Error(`Port '${config.name}' has no op '${op}'.`);
            return schema.result.parse(result);
        },
    };
}
/** Whether a contract was built here, and so can render its JSON schema. */
function isZodPortContract(contract) {
    return typeof contract.jsonSchema === 'object';
}
function fnv1a32(text, seed) {
    let hash = seed >>> 0;
    for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash;
}
function fnv1a64(text) {
    const a = fnv1a32(text, 0x811c9dc5).toString(16).padStart(8, '0');
    const b = fnv1a32(text, 0x2f9be6cd).toString(16).padStart(8, '0');
    return a + b;
}
//# sourceMappingURL=index.js.map